/**
 * tasks-store — React Query hooks pra tarefas com 3 modos de horário
 * (BUG-V2-016 / FEAT-V2 / T.1 backend mergeado em PR #20).
 *
 * Hooks:
 *   - useTasks(cardId, workflowSlug) — lista tarefas do card (ASC)
 *   - useCreateTask(cardId, workflowSlug) — POST com tratamento 409 conflict
 *   - useUpdateTask() — PUT
 *   - useDeleteTask() — DELETE soft
 *
 * Tratamento 409 calendar-conflict:
 *   - `createTask` faz POST. Em caso de 409, throws `ConflictError`
 *     contendo `{ conflicts, window, message }`.
 *   - UI captura via try/catch e mostra modal "agendar mesmo assim?"
 *   - Re-submit com `forceConflict: true` bypassa o gate.
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';
import { hojeBR } from './datas-brasilia';

// ─── Tipos ──────────────────────────────────────────────────────────────

export type TaskTimeMode = 'no_time' | 'start_only' | 'start_end';
export type TaskStatus = 'pendente' | 'em_progresso' | 'concluida' | 'cancelada';
export type TaskSyncStatus = 'pending' | 'synced' | 'failed' | 'disabled';

export interface Task {
  id: string;
  cardId: string;
  workflowSlug: string;
  activityTypeId: string | null;
  titulo: string;
  descricao: string | null;
  responsavelId: string | null;
  criadoPorId: string;
  status: TaskStatus;
  timeMode: TaskTimeMode;
  startAtIso: string | null;
  endAtIso: string | null;
  googleEventId: string | null;
  syncStatus: TaskSyncStatus;
  syncError: string | null;
  ultimaSyncIso: string | null;
  criadoEmIso: string;
  atualizadoEmIso: string;
  concluidaEmIso: string | null;
}

export interface CreateTaskInput {
  workflowSlug: string;
  activityTypeId?: string | null;
  titulo: string;
  descricao?: string | null;
  responsavelId?: string | null;
  timeMode: TaskTimeMode;
  startAtIso?: string | null;
  endAtIso?: string | null;
  forceConflict?: boolean;
}

export interface UpdateTaskInput {
  titulo?: string;
  descricao?: string | null;
  responsavelId?: string | null;
  status?: TaskStatus;
  timeMode?: TaskTimeMode;
  startAtIso?: string | null;
  endAtIso?: string | null;
  forceConflict?: boolean;
}

export interface ConflictingEvent {
  id: string;
  summary: string;
  start: string;
  end: string;
  htmlLink: string;
}

export interface ConflictPayload {
  conflicts: ConflictingEvent[];
  window: { start: string; end: string };
  message: string;
}

/**
 * Erro tipado pra 409 calendar-conflict. UI usa `instanceof TaskConflictError`
 * pra distinguir do erro genérico HTTP.
 */
export class TaskConflictError extends Error {
  constructor(public readonly payload: ConflictPayload) {
    super(payload.message);
    this.name = 'TaskConflictError';
  }
}

// ─── Keys ────────────────────────────────────────────────────────────────

export const TASK_KEYS = {
  list: (cardId: string, workflowSlug: string) =>
    ['v1', 'tasks', { cardId, workflowSlug }] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────

export function useTasks(cardId: string | null, workflowSlug: string | null) {
  return useQuery({
    queryKey: TASK_KEYS.list(cardId ?? '', workflowSlug ?? ''),
    queryFn: () =>
      api.get<Task[]>(
        `/api/v1/cards/${encodeURIComponent(cardId!)}/tasks?workflow=${encodeURIComponent(workflowSlug!)}`
      ),
    enabled: !!cardId && !!workflowSlug && hasToken(),
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────

interface ApiErrorShape {
  status?: number;
  /** ApiError em `src/lib/api.ts` usa `response`, não `body`. */
  response?: {
    error?: string;
    conflicts?: ConflictingEvent[];
    message?: string;
    window?: { start: string; end: string };
  };
}

function isCalendarConflictError(err: unknown): err is ApiErrorShape {
  if (!err || typeof err !== 'object') return false;
  const e = err as ApiErrorShape;
  return e.status === 409 && e.response?.error === 'calendar-conflict';
}

export function useTaskActions(cardId: string, workflowSlug: string) {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: TASK_KEYS.list(cardId, workflowSlug) });
  }, [qc, cardId, workflowSlug]);

  const createTask = useCallback(
    async (input: CreateTaskInput): Promise<Task> => {
      setPending('create');
      try {
        return await api.post<Task>(
          `/api/v1/cards/${encodeURIComponent(cardId)}/tasks`,
          input
        );
      } catch (err) {
        if (isCalendarConflictError(err)) {
          const resp = err.response!;
          throw new TaskConflictError({
            conflicts: resp.conflicts ?? [],
            window: resp.window ?? { start: '', end: '' },
            message: resp.message ?? 'Conflito de calendário',
          });
        }
        throw err;
      } finally {
        setPending(null);
        invalidate();
      }
    },
    [cardId, invalidate]
  );

  const updateTask = useCallback(
    async (taskId: string, input: UpdateTaskInput): Promise<Task> => {
      setPending(taskId);
      try {
        return await api.put<Task>(`/api/v1/tasks/${encodeURIComponent(taskId)}`, input);
      } catch (err) {
        if (isCalendarConflictError(err)) {
          const resp = err.response!;
          throw new TaskConflictError({
            conflicts: resp.conflicts ?? [],
            window: resp.window ?? { start: '', end: '' },
            message: resp.message ?? 'Conflito de calendário',
          });
        }
        throw err;
      } finally {
        setPending(null);
        invalidate();
      }
    },
    [invalidate]
  );

  const deleteTask = useCallback(
    async (taskId: string): Promise<void> => {
      setPending(taskId);
      try {
        await api.delete(`/api/v1/tasks/${encodeURIComponent(taskId)}`);
      } finally {
        setPending(null);
        invalidate();
      }
    },
    [invalidate]
  );

  return useMemo(
    () => ({ createTask, updateTask, deleteTask, pending }),
    [createTask, updateTask, deleteTask, pending]
  );
}

// ─── Helper: combina date YYYY-MM-DD + time HH:MM em ISO local ──────────

/**
 * Combina date `YYYY-MM-DD` + time `HH:MM` em ISO 8601 com offset local
 * do browser. Backend trata como timestamp absoluto.
 */
export function combineDateAndTime(date: string, time: string): string {
  // `2026-05-21T14:30` é interpretado como local time pelo JS Date
  const d = new Date(`${date}T${time}:00`);
  return d.toISOString();
}

/**
 * Extrai YYYY-MM-DD do ISO. Usado pra mode 'no_time' (envia só a data).
 */
export function isoToDateOnly(iso: string | null | undefined): string {
  if (!iso) return hojeBR();
  return iso.slice(0, 10);
}
